import { httpPost, httpGet } from "utils/httpRequest"

// AI Agent CRUD operations
const getAiAgentById = async (id) => {
  const resp = await httpPost("admin/aiAgent/getById", { id })
  return resp.data
}

const addAiAgent = async (agentData) => {
  const resp = await httpPost("admin/aiAgent/add", agentData)
  return resp.data
}

const editAiAgent = async (agentData) => {
  const resp = await httpPost("admin/aiAgent/edit", agentData)
  return resp.data
}

const deleteAiAgent = async (id) => {
  const resp = await httpPost("admin/aiAgent/delete", { id })
  return resp.data
}

const toggleAiAgentStatus = async (id) => {
  const resp = await httpPost("admin/aiAgent/toggleStatus", { id })
  return resp.data
}

const getAllActiveAiAgents = async () => {
  const resp = await httpPost("admin/aiAgent/getAllActive")
  return resp.data
}

const addAllFromVoices = async () => {
  const resp = await httpPost("admin/aiAgent/addAllFromVoices", {})
  return resp.data
}

const addFromVoice = async (id) => {
  const resp = await httpPost("admin/aiAgent/addFromVoice", { id })
  return resp.data
}

const updateLinkedVoices = async ({ agentId, voiceIds }) => {
  const resp = await httpPost('admin/aiAgent/updateLinkedVoices', { agentId, voiceIds })
  return resp.data
}

const listAiAgents = async ({
  keyword,
  isActive,
  specialty,
  tierProgramId,
  page = 1,
  pageSize = 200,
  sortField,
  sortOrder,
} = {}) => {
  const resp = await httpPost("admin/aiAgent/list", {
    keyword,
    isActive,
    specialty,
    tierProgramId,
    page,
    pageSize,
    sortField,
    sortOrder,
  })
  return resp.data
}

// AI Agent Knowledge Base operations - Updated to match API documentation
const getAiAgentKnowledgeBaseList = async ({ page, pageSize, agentId, contentType, keyword, processingStatus, sortField, sortOrder }) => {
  const resp = await httpPost("admin/aiAgentKnowledgeBase/list", {
    page,
    pageSize,
    agentId,
    contentType,
    keyword,
    processingStatus,
    sortField,
    sortOrder,
  })
  return resp.data
}

const getAiAgentKnowledgeBaseById = async (id) => {
  const resp = await httpPost("admin/aiAgentKnowledgeBase/getById", { id })
  return resp.data
}

const addAiAgentKnowledgeBase = async (knowledgeData) => {
  const resp = await httpPost("admin/aiAgentKnowledgeBase/add", knowledgeData)
  return resp.data
}

const editAiAgentKnowledgeBase = async (knowledgeData) => {
  const resp = await httpPost("admin/aiAgentKnowledgeBase/edit", knowledgeData)
  return resp.data
}

const deleteAiAgentKnowledgeBase = async (id) => {
  const resp = await httpPost("admin/aiAgentKnowledgeBase/delete", { id })
  return resp.data
}

const getAiAgentKnowledgeBaseByAgentId = async (id) => {
  const resp = await httpPost("admin/aiAgentKnowledgeBase/getByAgentId", { id })
  return resp.data
}

const searchAiAgentKnowledgeBase = async (searchParams) => {
  const resp = await httpPost("admin/aiAgentKnowledgeBase/search", searchParams)
  return resp.data
}

const ragSearchAiAgentKnowledgeBase = async (searchParams) => {
  const resp = await httpPost("admin/aiAgentKnowledgeBase/ragSearch", searchParams)
  return resp.data
}

const processPendingAiAgentKnowledgeBase = async () => {
  const resp = await httpPost("admin/aiAgentKnowledgeBase/processPending")
  return resp.data
}

const syncExternalAiAgentKnowledgeBaseSource = async (id) => {
  const resp = await httpPost("admin/aiAgentKnowledgeBase/syncExternalSource", { id })
  return resp.data
}

const syncExternalAiAgentKnowledgeBaseSources = async (agentId) => {
  const resp = await httpPost("admin/aiAgentKnowledgeBase/syncExternalSources", {
    agentId,
  })
  return resp.data
}

const startAiAgentKnowledgeBaseOAuth = async ({ provider, returnOrigin }) => {
  const resp = await httpPost("admin/aiAgentKnowledgeBase/oauth/start", {
    provider,
    returnOrigin,
  })
  return resp.data
}

const listAiAgentKnowledgeBaseOAuthCredentials = async (provider) => {
  const resp = await httpPost("admin/aiAgentKnowledgeBase/oauth/credentials", {
    provider,
  })
  return resp.data
}

const reprocessFailedAiAgentKnowledgeBase = async (agentId) => {
  const url = agentId ? `admin/aiAgentKnowledgeBase/reprocessFailed?agentId=${agentId}` : 'admin/aiAgentKnowledgeBase/reprocessFailed'
  const resp = await httpPost(url)
  return resp.data
}

const updateProcessingStatusAiAgentKnowledgeBase = async (id, status, error = null) => {
  const resp = await httpPost("admin/aiAgentKnowledgeBase/updateProcessingStatus", {
    id,
    status,
    error
  })
  return resp.data
}

const bulkOperationAiAgentKnowledgeBase = async (operation, ids, agentId) => {
  const resp = await httpPost("admin/aiAgentKnowledgeBase/bulkOperation", {
    operation,
    ids,
    agentId
  })
  return resp.data
}

const getAiAgentKnowledgeBaseStats = async (agentId) => {
  const resp = await httpGet(`admin/aiAgentKnowledgeBase/stats${agentId ? `?agentId=${agentId}` : ''}`)
  return resp.data
}

const getAiAgentKnowledgeBaseContentTypes = async () => {
  const resp = await httpGet("admin/aiAgentKnowledgeBase/contentTypes")
  return resp.data
}

const getAiAgentKnowledgeBaseProcessingStatuses = async () => {
  const resp = await httpGet("admin/aiAgentKnowledgeBase/processingStatuses")
  return resp.data
}

// File upload and download functions for AI Agent Knowledge Base
const uploadAiAgentKnowledgeBaseFile = async (file, agentId, metadata) => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('agentId', agentId)
  formData.append('contentType', metadata.contentType || 'file')
  formData.append('contentTitle', metadata.contentTitle || file.name)
  formData.append('contentDescription', metadata.contentDescription || '')
  formData.append('processImmediately', metadata.processImmediately !== false)
  
  // Add optional parameters if provided
  if (metadata.chunkSize) formData.append('chunkSize', metadata.chunkSize)
  if (metadata.chunkOverlap) formData.append('chunkOverlap', metadata.chunkOverlap)
  if (metadata.metadata) formData.append('metadata', JSON.stringify(metadata.metadata))
  
  const resp = await httpPost("admin/aiAgentKnowledgeBase/upload", formData, {
    onUploadProgress: (progressEvent) => {
      if (metadata.onProgress) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        metadata.onProgress(progress)
      }
    }
  })
  return resp.data
}

const downloadAiAgentKnowledgeBaseFile = async (fileId, fileName) => {
  const resp = await httpGet(`admin/aiAgentKnowledgeBase/downloadFile/${fileId}`, {
    responseType: 'blob'
  })
  
  // Create download link
  const url = window.URL.createObjectURL(new Blob([resp.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', fileName || 'download')
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
  
  return resp.data
}

// Chunked upload functions for large files
const uploadAiAgentKnowledgeBaseChunk = async (chunk, uploadId, chunkIndex, totalChunks, agentId, metadata, originalFileName, originalFileSize, onProgress) => {
  const formData = new FormData()
  formData.append('file', chunk, `chunk_${chunkIndex}`)
  formData.append('uploadId', uploadId)
  formData.append('chunkIndex', chunkIndex.toString())
  formData.append('totalChunks', totalChunks.toString())
  formData.append('agentId', agentId)
  formData.append('contentType', metadata.contentType || 'file')
  formData.append('contentTitle', metadata.contentTitle || 'Unknown')
  formData.append('contentDescription', metadata.contentDescription || '')
  formData.append('processImmediately', metadata.processImmediately !== false)
  
  // Required fields for backend
  formData.append('originalFileName', originalFileName)
  formData.append('originalFileSize', originalFileSize.toString())
  
  // Add optional parameters if provided
  if (metadata.chunkSize) formData.append('chunkSize', metadata.chunkSize)
  if (metadata.chunkOverlap) formData.append('chunkOverlap', metadata.chunkOverlap)
  if (metadata.metadata) formData.append('metadata', JSON.stringify(metadata.metadata))
  
  const resp = await httpPost("admin/aiAgentKnowledgeBase/uploadChunk", formData, {
    onUploadProgress: (progressEvent) => {
      if (onProgress) {
        const chunkProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        onProgress(chunkIndex, chunkProgress, totalChunks)
      }
    }
  })
  return resp.data
}

const cancelAiAgentKnowledgeBaseUpload = async (uploadId) => {
  const resp = await httpPost("admin/aiAgentKnowledgeBase/cancelChunkedUpload", { uploadId })
  return resp.data
}

const uploadAiAgentKnowledgeBaseFileChunked = async (file, agentId, metadata, options = {}) => {
  const {
    chunkSize = 20 * 1024 * 1024, // 20MB default
    onProgress = null,
    onChunkComplete = null,
    onError = null,
    onCancel = null
  } = options

  // Check if file is large enough to warrant chunking (25MB threshold)
  const CHUNKING_THRESHOLD = 25 * 1024 * 1024 // 25MB
  
  if (file.size < CHUNKING_THRESHOLD) {
    // Use regular upload for smaller files
    return await uploadAiAgentKnowledgeBaseFile(file, agentId, {
      ...metadata,
      onProgress
    })
  }

  // Generate unique upload ID
  const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  
  try {
    // Split file into chunks
    const chunks = []
    let start = 0
    
    while (start < file.size) {
      const end = Math.min(start + chunkSize, file.size)
      const chunk = file.slice(start, end)
      chunks.push(chunk)
      start = end
    }
    
    const totalChunks = chunks.length
    
    console.log(`Starting chunked upload: ${file.name} (${totalChunks} chunks, ${Math.round(file.size / 1024 / 1024)}MB)`)
    
    // Upload chunks sequentially
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i]
      
      try {
        const response = await uploadAiAgentKnowledgeBaseChunk(
          chunk,
          uploadId,
          i,
          totalChunks,
          agentId,
          metadata,
          file.name,
          file.size,
          onProgress
        )
        
        if (onChunkComplete) {
          onChunkComplete(i + 1, totalChunks, response)
        }
        
        console.log(`Chunk ${i + 1}/${totalChunks} uploaded successfully`)
        
      } catch (error) {
        console.error(`Error uploading chunk ${i + 1}/${totalChunks}:`, error)
        
        if (onError) {
          onError(error, i + 1, totalChunks)
        }
        
        // Cancel the upload if a chunk fails
        try {
          await cancelAiAgentKnowledgeBaseUpload(uploadId)
        } catch (cancelError) {
          console.error('Error canceling upload:', cancelError)
        }
        
        throw error
      }
    }
    
    // Return success response (the backend will process the complete file)
    return {
      success: true,
      uploadId,
      totalChunks,
      message: 'All chunks uploaded successfully. Processing will begin automatically.'
    }
    
  } catch (error) {
    console.error('Chunked upload failed:', error)
    
    if (onCancel) {
      onCancel(uploadId)
    }
    
    throw error
  }
}

const listAvailableLlmModels = async () => {
  const resp = await httpGet("admin/aiAgent/availableLlmModels")
  return resp.data
}

const listAvailableRealtimeModels = async () => {
  const resp = await httpGet("admin/aiAgent/availableRealtimeModels")
  return resp.data
}

const uploadTranscriptBulkImportZip = async (zipFile, onProgress) => {
  const formData = new FormData()
  formData.append('file', zipFile)

  const resp = await httpPost("admin/transcriptBulkImport/upload", formData, {
    onUploadProgress: (progressEvent) => {
      if (onProgress) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        onProgress(progress)
      }
    }
  })
  return resp.data
}

const getTranscriptBulkImportStatus = async (jobId) => {
  const resp = await httpGet(`admin/transcriptBulkImport/status/${jobId}`)
  return resp.data
}

export {
  getAiAgentById,
  addAiAgent,
  editAiAgent,
  deleteAiAgent,
  toggleAiAgentStatus,
  getAllActiveAiAgents,
  listAiAgents,
  getAiAgentKnowledgeBaseList,
  getAiAgentKnowledgeBaseById,
  addAiAgentKnowledgeBase,
  editAiAgentKnowledgeBase,
  deleteAiAgentKnowledgeBase,
  getAiAgentKnowledgeBaseByAgentId,
  searchAiAgentKnowledgeBase,
  ragSearchAiAgentKnowledgeBase,
  processPendingAiAgentKnowledgeBase,
  syncExternalAiAgentKnowledgeBaseSource,
  syncExternalAiAgentKnowledgeBaseSources,
  startAiAgentKnowledgeBaseOAuth,
  listAiAgentKnowledgeBaseOAuthCredentials,
  reprocessFailedAiAgentKnowledgeBase,
  updateProcessingStatusAiAgentKnowledgeBase,
  bulkOperationAiAgentKnowledgeBase,
  getAiAgentKnowledgeBaseStats,
  getAiAgentKnowledgeBaseContentTypes,
  getAiAgentKnowledgeBaseProcessingStatuses,
  uploadAiAgentKnowledgeBaseFile,
  downloadAiAgentKnowledgeBaseFile,
  uploadAiAgentKnowledgeBaseChunk,
  cancelAiAgentKnowledgeBaseUpload,
  uploadAiAgentKnowledgeBaseFileChunked,
  uploadTranscriptBulkImportZip,
  getTranscriptBulkImportStatus,
  addAllFromVoices,
  addFromVoice,
  updateLinkedVoices,
  listAvailableLlmModels,
  listAvailableRealtimeModels,
}
